// Ported from eb-ui `src/legacy/CreatorCollectionsVerticalEventCard` — the
// vertical event card the collection's own event list renders.
//
// Mapping (per the import table):
//   - `@eventbrite/event-renderer` → the sanctioned
//     `@eventbrite/consumer-search.event-renderer` (same primitives; the
//     MISSING exports are inlined):
//       * `EventCardRecipeProps` (missing) → a local `EventCardRecipeProps`
//         (the eb-ui `recipes/types.ts` shape — the call-sites pass exactly
//         event/locale/isAuthenticated/affCode/statsigLocationString/
//         moreActions/isLazyImage).
//       * `VERTICAL_EVENT_CARD_IMAGE_CONFIG` (missing) → inlined (the literal
//         from the eb-ui `EventCardImage/constants.ts`).
//     Note: the eb-ui original passed `repeatingInstanceCount` to
//     `useGetFormattedEventDateTimeString`, but that param is NOT part of the
//     sanctioned hook's config (its `GetFormattedEventDateString`) — the
//     sanctioned hook derives the "and X more" suffix from
//     `additionalSessionsCount` instead, so it is omitted here.
import {
    EventCardActions,
    EventCardBadge,
    EventCardDate,
    EventCardDetails,
    EventCardImage,
    EventCardLink,
    EventCardLocation,
    EventCardPrice,
    EventCardPromoted,
    EventCardSaveAction,
    EventCardTitle,
    EventCardTracking,
    getEventCardLinkProps,
    getFormattedSalesStatus,
    HORIZONTAL_LEFT_CARD_BADGE_STYLE,
    useFormattedLocationString,
    useGetFormattedEventDateTimeString,
    useMinPriceString,
    VerticalEventCard,
    type FormattedEvent,
} from '../lib/eventRenderer';
import React from 'react';

const VERTICAL_EVENT_CARD_IMAGE_CONFIG = {
    '--image-aspect-ratio': '2',
    '--image-aspect-ratio-mobile': '2',
    '--image-width': '100%',
    '--image-width-mobile': '100%',
};

// Missing from the sanctioned package: the eb-ui `recipes/types.ts`
// `EventCardRecipeProps` (the card recipe's call-site contract).
export interface EventCardRecipeProps {
    event: FormattedEvent;
    locale: string;
    isAuthenticated: boolean;
    affCode: string;
    statsigLocationString: string;
    moreActions?: React.ReactNode;
    isLazyImage?: boolean;
    isRaised?: boolean;
    onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
    bucketLabel?: string;
}

export const CreatorCollectionsVerticalEventCard: React.FC<
    EventCardRecipeProps
> = (props) => {
    const {
        event,
        locale,
        isRaised,
        statsigLocationString,
        moreActions,
        affCode,
        onClick,
    } = props;

    const linkProps = getEventCardLinkProps({
        affCode: affCode,
        event,
        onClick,
    });

    const formattedDateString = useGetFormattedEventDateTimeString({
        startDate: event?.startDate,
        startTime: event?.startTime,
        timezone: event?.timezone,
        repeatingInstanceCount: event?.repeatingInstanceCount,
        locale: locale,
        useUserTimezone: event.isOnlineEvent,
        dateTimeDelimeter: '•',
    });

    //Only show active Sales Status (sold out etc)
    //For non-repeating events to prevent incorrect signals.
    const formattedSalesStatus = getFormattedSalesStatus(event);

    const eventCardTrackingProps = {
        statsigLocationString: statsigLocationString,
        event,
    };

    const formattedPriceString = useMinPriceString({
        price: event?.minPrice,
        locale: locale,
    });

    const formattedLocationString = useFormattedLocationString({
        venue: event.venue,
        style: 'venue',
    });

    return (
        <VerticalEventCard isRaised={isRaised}>
            <EventCardTracking {...eventCardTrackingProps}>
                <EventCardBadge style={HORIZONTAL_LEFT_CARD_BADGE_STYLE}>
                    {formattedSalesStatus}
                </EventCardBadge>
                <EventCardLink {...linkProps}>
                    <EventCardImage
                        name={event.name}
                        loading={props.isLazyImage ? 'lazy' : 'eager'}
                        tokens={{
                            ...VERTICAL_EVENT_CARD_IMAGE_CONFIG,
                            '--image-background-color': event.edgeColor,
                        }}
                        src={event.imageUrl}
                        imageSizes={event.imageSizes}
                        preferredSize="medium"
                    />
                </EventCardLink>

                <EventCardDetails
                    space="xxs"
                    tokens={{
                        '--EventCardDetailsPadding': '16px 8px 12px 12px',
                        '--EventCardDetailsFlexGrow': '2',
                        '--EventCardDetailsPosition': 'relative',
                    }}
                >
                    <EventCardPromoted isPromoted={event.isPromoted} />
                    <EventCardTitle
                        className="event-card__clamp-line--two"
                        title={event.name}
                        color="neutral-800"
                    />
                    <EventCardDate
                        className="event-card__clamp-line--one"
                        formattedDateString={formattedDateString}
                        variant="body-md"
                    />
                    <EventCardLocation
                        className="event-card__clamp-line--one"
                        formattedLocationString={formattedLocationString}
                    />
                    <EventCardPrice
                        color="neutral-800"
                        isFree={event.isFree}
                        formattedPriceString={formattedPriceString}
                        variant="body-md-bold"
                    />
                    <EventCardActions
                        tokens={{
                            '--EventCardActionsPosition': 'absolute',
                            '--EventCardActionsTop': '-22px',
                            '--EventCardActionsRight': '8px',
                            '--EventCardActionsGap': '8px',
                        }}
                        actionStyleOverride="outline"
                    >
                        <EventCardSaveAction
                            isAuthenticated={!!props.isAuthenticated}
                            savedByYou={event.savedByYou}
                            id={event.id}
                            eventName={event.name}
                            imageUrl={event.imageUrl}
                        />
                        {moreActions}
                    </EventCardActions>
                </EventCardDetails>
            </EventCardTracking>
        </VerticalEventCard>
    );
};
